import { formatCountyDisplay } from "encamp-shared/src/utils/county";
import {
  FacilityRcraExemptBoilerActivity,
  FacilityRcraProfileFragment,
  FacilityRcraRecognizedTraderActivity,
  FacilityRcraRecyclerActivity,
  FacilityRcraSlabActivity,
  FacilityRcraSubpartKEntityType,
  FacilityRcraSubpartPActivity,
  FacilityRcraTransporterActivity,
  FacilityRcraUniversalWasteType,
  FacilityRcraUsedOilMarketerActivity,
  FacilityRcraUsedOilProcessorActivity,
  FacilityRcraUsedOilTransporterActivity,
  RcraInfoLandType,
} from "generated-graphql/graphql";

export const RCRA_LAND_TYPE_LABELS: Record<RcraInfoLandType, string> = {
  [RcraInfoLandType.County]: "County",
  [RcraInfoLandType.District]: "District",
  [RcraInfoLandType.Federal]: "Federal",
  [RcraInfoLandType.Tribal]: "Tribal",
  [RcraInfoLandType.Municipal]: "Municipal",
  [RcraInfoLandType.Other]: "Other",
  [RcraInfoLandType.Private]: "Private",
  [RcraInfoLandType.State]: "State",
};

export const RCRA_RECYCLER_ACTIVITY_LABELS: Record<FacilityRcraRecyclerActivity, string> = {
  [FacilityRcraRecyclerActivity.StoresPriorToRecycling]: "Stores prior to recycling",
  [FacilityRcraRecyclerActivity.DoesNotStorePriorToRecycling]: "Does not store prior to recycling",
};

export const RCRA_EXEMPT_BOILER_ACTIVITY_LABELS: Record<FacilityRcraExemptBoilerActivity, string> =
  {
    [FacilityRcraExemptBoilerActivity.SmallQuantityOnsiteBurnerExemption]:
      "Small quantity on-site burner exemption",
    [FacilityRcraExemptBoilerActivity.SmeltingMeltingRefiningFurnaceExemption]:
      "Smelting, melting, refining furnace exemption",
  };

export const RCRA_TRANSPORTER_ACTIVITY_LABELS: Record<FacilityRcraTransporterActivity, string> = {
  [FacilityRcraTransporterActivity.Transporter]: "Transporter",
  [FacilityRcraTransporterActivity.TransferFacility]: "Transfer facility",
};

export const RCRA_RECOGNIZED_TRADER_LABELS: Record<FacilityRcraRecognizedTraderActivity, string> = {
  [FacilityRcraRecognizedTraderActivity.Importer]: "Importer",
  [FacilityRcraRecognizedTraderActivity.Exporter]: "Exporter",
};

export const RCRA_SLAB_LABELS: Record<FacilityRcraSlabActivity, string> = {
  [FacilityRcraSlabActivity.Importer]: "Importer",
  [FacilityRcraSlabActivity.Exporter]: "Exporter",
};

export const RCRA_UNIVERSAL_WASTE_TYPE_LABELS: Record<FacilityRcraUniversalWasteType, string> = {
  [FacilityRcraUniversalWasteType.Batteries]: "Batteries",
  [FacilityRcraUniversalWasteType.MercuryContainingEquipment]: "Mercury-containing equipment",
  [FacilityRcraUniversalWasteType.Lamps]: "Lamps",
  [FacilityRcraUniversalWasteType.Pesticides]: "Pesticides",
};

export const RCRA_USED_OIL_TRANSPORTER_LABELS: Record<
  FacilityRcraUsedOilTransporterActivity,
  string
> = {
  [FacilityRcraUsedOilTransporterActivity.Transporter]: "Transporter",
  [FacilityRcraUsedOilTransporterActivity.TransferFacility]: "Transfer facility",
};

export const RCRA_USED_OIL_PROCESSOR_LABELS: Record<FacilityRcraUsedOilProcessorActivity, string> =
  {
    [FacilityRcraUsedOilProcessorActivity.Processor]: "Processor",
    [FacilityRcraUsedOilProcessorActivity.ReRefiner]: "Re-refiner",
  };

export const RCRA_USED_OIL_MARKETER_LABELS: Record<FacilityRcraUsedOilMarketerActivity, string> = {
  [FacilityRcraUsedOilMarketerActivity.DirectsShipmentToOffSpecBurner]:
    "Directs shipment to off-spec burner",
  [FacilityRcraUsedOilMarketerActivity.FirstClaimsOnSpec]: "First claims on-spec",
};

export const RCRA_SUBPART_P_ACTIVITY_LABELS: Record<FacilityRcraSubpartPActivity, string> = {
  [FacilityRcraSubpartPActivity.HealthcareFacility]: "Healthcare facility",
  [FacilityRcraSubpartPActivity.ReverseDistributor]: "Reverse distributor",
};

export const RCRA_SUBPART_K_ENTITY_TYPE_LABELS: Record<FacilityRcraSubpartKEntityType, string> = {
  [FacilityRcraSubpartKEntityType.CollegeOrUniversity]: "College or university",
  [FacilityRcraSubpartKEntityType.TeachingHospital]: "Teaching hospital",
  [FacilityRcraSubpartKEntityType.NonprofitInstitute]: "Nonprofit institute",
};

export function selectItemsFromLabels<T extends string>(
  labels: Record<T, string>
): { value: T; display: string }[] {
  // Narrow Object.entries' widened keys back to T at the boundary.
  const entries = Object.entries(labels) as [T, string][];
  return entries.map(([value, display]) => ({ value, display }));
}

export function formatBoolean(value: boolean | null | undefined): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
}

export function formatList(values: string[] | null | undefined, labels: Record<string, string>) {
  if (!values || values.length === 0) return "";
  return values.map((v) => labels[v] ?? v).join(", ");
}

function normalizeCountry(value: string): string {
  if (value === "US" || value === "USA") return "United States";
  return value;
}

function formatAddressOneLiner(
  street1?: string | null,
  street2?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null
) {
  const street = [street1, street2].filter(Boolean).join(" ");
  const cityState = [city, state].filter(Boolean).join(", ");
  return [street, cityState, zip].filter(Boolean).join(" ");
}

export function formatRcraSiteLocation(profile: FacilityRcraProfileFragment | null): string {
  if (!profile) return "";

  const lines: string[] = [];

  const oneLiner = formatAddressOneLiner(
    profile.rcraStreetAddress1,
    profile.rcraStreetAddress2,
    profile.rcraCity,
    profile.rcraState,
    profile.rcraZipCode
  );
  if (oneLiner) lines.push(oneLiner);

  if (profile.rcraCounty) lines.push(formatCountyDisplay(profile.rcraCounty));
  if (profile.rcraCountry) lines.push(normalizeCountry(profile.rcraCountry));

  if (profile.rcraLatitude != null || profile.rcraLongitude != null) {
    const lat = profile.rcraLatitude?.toString() ?? "—";
    const lng = profile.rcraLongitude?.toString() ?? "—";
    const primarySuffix = profile.useLatLongAsPrimary ? " (used as primary address)" : "";
    lines.push(`Lat/Long: ${lat}, ${lng}${primarySuffix}`);
  }

  return lines.join("\n");
}

export function formatRcraSiteMailingAddress(profile: FacilityRcraProfileFragment | null): string {
  if (!profile) return "";

  const lines: string[] = [];

  const oneLiner = formatAddressOneLiner(
    profile.rcraMailingStreetAddress1,
    profile.rcraMailingStreetAddress2,
    profile.rcraMailingCity,
    profile.rcraMailingState,
    profile.rcraMailingZipCode
  );
  if (oneLiner) lines.push(oneLiner);

  if (profile.rcraMailingCountry) lines.push(normalizeCountry(profile.rcraMailingCountry));

  return lines.join("\n");
}
